import { MarkerFeature, MarkerGeometry, MarkerProperties } from "../../../utils/models/MarkerFeature";
import { LineStringFeature } from "../../../utils/models/LineStringFeature";
import { Route } from "../../../utils/models/Route";
import { generateID } from "../../../utils/helpers/IDGenerator";
import { sliceByDistance, nearestOnTrail, cumulativeDistances, LockedBoundary } from "../utils/masterTrail";
import { orderedDayStages } from "./stage-connectors";

/**
 * Locked-mode day route assembly — pure logic (no map, no store, no
 * network), so it can be unit tested. Fetching a boundary's detour line is
 * the caller's job (store/modules/actions/locked-boundary-drag.ts); this
 * module only assembles an already-routed day from two boundaries.
 */

export interface FrozenTrail {
  coordinates: number[][];
  cumDist: number[];
}

function markerAt(coord: number[]): MarkerFeature {
  return new MarkerFeature(
    new MarkerGeometry([coord[0], coord[1], coord[2] ?? 0]),
    new MarkerProperties(generateID(), true, Date.now().toString(), coord[2] ?? 0)
  );
}

/**
 * Assemble one day's markers/lineStrings from its two boundaries:
 * [detour arriving from startBoundary's off-trail point, reversed] +
 * [a literal slice of the frozen trail between the two gates] +
 * [detour departing to endBoundary's off-trail point].
 * The on-trail slice is always taken from `trail` — the day's route never
 * diverges from it except for the two optional detour pieces at its ends.
 */
export function buildLockedDayRoute(
  trail: FrozenTrail,
  startBoundary: LockedBoundary,
  endBoundary: LockedBoundary
): { markers: MarkerFeature[]; lineStrings: LineStringFeature[] } {
  const lineStrings: LineStringFeature[] = [];

  if (startBoundary.offTrailPoint && startBoundary.detourLine) {
    lineStrings.push(startBoundary.detourLine.reverse());
  }

  const mainSlice = sliceByDistance(trail.coordinates, trail.cumDist, startBoundary.distanceM, endBoundary.distanceM);
  lineStrings.push(LineStringFeature.coordsOnlyFeature(mainSlice));

  if (endBoundary.offTrailPoint && endBoundary.detourLine) {
    lineStrings.push(endBoundary.detourLine);
  }

  const markers: MarkerFeature[] = [markerAt(lineStrings[0].lineStringGeometry.coordinates[0])];
  for (const ls of lineStrings) {
    const coords = ls.lineStringGeometry.coordinates;
    markers.push(markerAt(coords[coords.length - 1]));
  }

  return { markers, lineStrings };
}

/**
 * Build ONE combined LineString between two arbitrary gates on the frozen
 * trail — the same slice-plus-optional-detour shape as buildLockedDayRoute,
 * but concatenated into a single feature instead of separate detour/slice/
 * detour pieces. Used for rebuilding the one or two segments touching an
 * interior control point (store/modules/actions/locked-boundary-drag.ts's
 * applyLockedMarkerDrag) — unlike a day boundary, an interior point has no
 * persisted slot to track a detour separately, so there's nothing that needs
 * the pieces kept apart; one feature keeps the route's marker/lineString
 * count invariant (markers.length === lineStrings.length + 1) intact, so
 * inserting it never shifts any other marker's index.
 */
export function buildLockedSegmentLineString(
  trail: FrozenTrail,
  startGate: LockedBoundary,
  endGate: LockedBoundary
): LineStringFeature {
  const coords: number[][] = [];

  if (startGate.offTrailPoint && startGate.detourLine) {
    coords.push(...startGate.detourLine.reverse().lineStringGeometry.coordinates);
  }

  const mainSlice = sliceByDistance(trail.coordinates, trail.cumDist, startGate.distanceM, endGate.distanceM);
  coords.push(...(coords.length > 0 ? mainSlice.slice(1) : mainSlice));

  if (endGate.offTrailPoint && endGate.detourLine) {
    coords.push(...endGate.detourLine.lineStringGeometry.coordinates.slice(1));
  }

  return LineStringFeature.coordsOnlyFeature(coords);
}

/**
 * Derive each internal day boundary from the CURRENT routes' actual split
 * points — used when locking, so enabling lock never redraws/re-splits any
 * existing route (unlike applyDayCount's even re-slice, which is only for an
 * explicit day-count change). Each day's own end marker already sits
 * (almost) exactly on the just-derived frozen trail, so nearestOnTrail
 * resolves its true distance with ~0 off-trail error.
 */
export function deriveBoundariesFromRoutes(
  routes: Route[],
  trail: FrozenTrail
): { [dayNumber: number]: LockedBoundary } {
  const routesByKey = Object.fromEntries(routes.map(route => [route.id, route]));
  const stages = orderedDayStages(routesByKey);
  const boundaries: { [dayNumber: number]: LockedBoundary } = {};

  for (let i = 0; i < stages.length - 1; i++) {
    const endMarker = stages[i].lastRoute.markers[stages[i].lastRoute.markers.length - 1];
    if (!endMarker) continue;
    const [lon, lat] = endMarker.markerGeometry.coordinates;
    const gate = nearestOnTrail(trail.coordinates, trail.cumDist, lon, lat);
    boundaries[stages[i].dayNumber] = { distanceM: gate.distanceM, offTrailPoint: null, detourLine: null };
  }

  return boundaries;
}

/**
 * Reverse the frozen master trail and its day boundaries in place, for when the
 * WHOLE itinerary is reversed while guided/locked mode is on. The lock pins the
 * hike to a particular LINE, not to a direction, so a reversal keeps the lock
 * and carries the frozen line around with it.
 *
 * This reverses the stored trail rather than re-deriving one from the reversed
 * routes, because re-deriving is lossy in two ways: deriveBoundariesFromRoutes
 * discards offTrailPoint/detourLine, and deriveMasterTrail would bake a detour
 * that a previous drag left in a day's own geometry into the trail itself (the
 * exact thing freezing exists to prevent).
 *
 * `distanceM` is measured from the trail's start, so every gate becomes
 * `total - distanceM`. offTrailPoint and detourLine are direction-free — a
 * coordinate, and a gate-to-item line — so they carry over untouched.
 *
 * Boundary keys do NOT move the way day numbers do. A boundary keyed by day d
 * marks the junction between d and the day after it; once the labels are
 * mirrored, that same junction is the END of whatever the following day became.
 * Hence the lookup through `orderedDays` rather than a straight `dayMirror` hit.
 */
export function reverseLockedTrailInPlace(
  state: {
    lockedMasterTrail?: FrozenTrail | null;
    lockedBoundaries?: { [dayNumber: number]: LockedBoundary };
  },
  orderedDays: number[],
  dayMirror: Map<number, number>
): void {
  const trail = state.lockedMasterTrail;
  if (!trail || trail.coordinates.length < 2) return;

  const total = trail.cumDist[trail.cumDist.length - 1] ?? 0;
  const coordinates = [...trail.coordinates].reverse();
  state.lockedMasterTrail = { coordinates, cumDist: cumulativeDistances(coordinates) };

  const boundaries = state.lockedBoundaries;
  if (!boundaries) return;

  const next: { [dayNumber: number]: LockedBoundary } = {};
  Object.keys(boundaries).forEach(key => {
    const day = Number(key);
    const boundary = boundaries[day];
    const followingDay = orderedDays[orderedDays.indexOf(day) + 1];
    const movedKey = followingDay !== undefined ? dayMirror.get(followingDay) : undefined;
    next[movedKey ?? day] = { ...boundary, distanceM: Math.max(0, total - boundary.distanceM) };
  });
  state.lockedBoundaries = next;
}
